import { CheckCircleOutlined, DeleteOutlined, DollarOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import { Position } from '@common';
import { BlackButton, InputMask, PrimaryButton } from '@components/atoms';
import { AddJobPositionModal } from '@components/molecules';
import { FILE_ACCEPT_STRING, validateFileFormat } from '@helpers';
import { useAuth, useJobApplications, useJobPositions } from '@hooks';
import { Button, Col, Form, Input, message, Modal, Row, Skeleton, Tag, Tooltip, Typography, Upload } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles.css';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

interface FormValues {
  fullName: string;
  email: string;
  phone: string;
  positionId: string;
  message: string;
}

type FilterType = 'All' | 'Operations' | 'Management' | 'Snow Removal' | 'Maintenance' | 'Administration';

export const CareersPage = () => {
  const { profile } = useAuth();
  const { jobPositions, addJobPosition, updateJobPosition, softDeleteJobPosition } = useJobPositions();
  const { createJobApplication } = useJobApplications();
  const [activeFilter, setActiveFilter] = useState<FilterType>('All');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState<string | null>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [form] = Form.useForm();
  const [isThankYouVisible, setIsThankYouVisible] = useState(false);
  const [appliedPositionTitle, setAppliedPositionTitle] = useState('');
  const navigate = useNavigate();

  const isAuthenticated = !!profile.data;

  const filteredPositions = activeFilter === 'All'
    ? jobPositions.data || []
    : (jobPositions.data || []).filter(position => position.department === activeFilter);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (isThankYouVisible) {
      timer = setTimeout(() => {
        handleCloseThankYou();
      }, 5000); // 5 segundos
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isThankYouVisible]);


  const onFinish = (values: FormValues) => {
    if (!resumeFile) {
      message.error('Please attach your resume');
      return;
    }


    const positionExists = jobPositions.data?.some(position => position.id === values.positionId);
    if (!positionExists) {
      message.error('The selected position is not available. Please select a different position.');
      return;
    }

    const selectedPosition = jobPositions.data?.find(pos => pos.id === values.positionId);
    if (selectedPosition) {
      setAppliedPositionTitle(selectedPosition.title);

      const positionIdToSend = selectedPosition.id;
      const formData = {
        ...values,
        positionId: positionIdToSend
      };

      createJobApplication.mutate(
        {
          data: formData,
          resume: resumeFile
        },
        {
          onSuccess: () => {
            setIsThankYouVisible(true);
          },
          onError: (error: any) => {
            console.error('Error submitting application:', error);
            const errorMessage = error?.response?.data?.message || 'Unable to submit your application. Please try again.';
            message.error(errorMessage);
          }
        }
      );
    } else {
      message.error('Position information not found. Please try again.');
    }
  };

  const handleCloseThankYou = () => {
    setIsThankYouVisible(false);
    form.resetFields();
    setResumeFile(null);
  };

  // Handler for file uploads
  const handleResumeUpload = (file: UploadFile) => {
    if (validateFileFormat(file)) {
      setResumeFile(file as unknown as File);
    }
    return false;
  };

  const handleAddPosition = (position: Position) => {
    addJobPosition.mutate(position, {
      onSuccess: () => {
        setIsAddModalOpen(false);
        message.success('Position added successfully!');
      },
      onError: (error) => {
        console.error('Error adding position:', error);
        message.error('Failed to add position. Please try again.');
      }
    });
  };

  const handleUpdatePosition = (position: Partial<Position>) => {
    if (selectedPositionId) {
      updateJobPosition.mutate(
        {
          id: selectedPositionId,
          data: position
        },
        {
          onSuccess: () => {
            setIsEditModalOpen(false);
            setSelectedPositionId(null);
            message.success('Position updated successfully!');
          },
          onError: (error) => {
            console.error('Error updating position:', error);
            message.error('Failed to update position. Please try again.');
          }
        }
      );
    } else {
      message.error('No position selected for update');
    }
  };

  const confirmDelete = (id: string) => {
    setPositionToDelete(id);
  };

  const handleDelete = () => {
    if (positionToDelete) {
      softDeleteJobPosition.mutate(positionToDelete, {
        onSuccess: () => {
          setPositionToDelete(null);
          message.success('Position removed successfully!');
        }
      });
    }
  };

  const viewPositionDetails = (id: string) => {
    navigate(`/careers/${id}`);
  };

  const handleEditPosition = (id: string) => {
    const position = jobPositions.data?.find(pos => pos.id === id);

    if (position) {
      setSelectedPositionId(id);
      setIsEditModalOpen(true);
    } else {
      message.error('Error loading position data for editing');
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative w-full h-[400px]">
        <div className="careers-bg opacity-40 absolute inset-0 w-full h-full"></div>
        <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 md:py-16 lg:py-20 z-10">
          <Title level={1} className="font-android !font-normal text-yellow-principal text-center">
            JOIN OUR TEAM
          </Title>
          <div className="max-w-3xl mx-auto">
            <Paragraph className="font-barlow text-gray-300 text-center md:text-lg lg:!text-xl">
              Build your career with a company that values skill, safety, and innovation
            </Paragraph>
          </div>
        </div>
      </section>

      <section className="w-full pt-8 bg-second-black-carbon relative pb-24">
        {/* Decorative image for transition effect */}
        <div className="absolute bottom-0 left-0 w-full h-auto z-10">
          <img
            src="/assets/images/between-sections-down-1.png"
            alt=""
            className="w-full h-auto object-contain"
          />
        </div>
        <div className="container mx-auto px-4 relative z-20">
          <div>
            <Title level={2} className="font-android !font-normal text-yellow-principal text-center mt-4">CURRENT OPENINGS</Title>
            <div className="max-w-3xl mx-auto">
              <Paragraph className="font-barlow text-gray-300 text-center pb-12">
                Explore opportunities to join our growing team and build a rewarding career
              </Paragraph>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <div className="flex flex-wrap justify-start gap-2 mb-4 md:mb-0">
              {(['All', 'Operations', 'Management', 'Snow Removal', 'Maintenance', 'Administration'] as FilterType[]).map((filter) => (
                <Button
                  key={filter}
                  type="default"
                  className={`!border-none !font-['Android_101'] filter-button ${activeFilter === filter ? 'filter-active' : ''}`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter}
                </Button>
              ))}
            </div>

            {isAuthenticated && (
              <PrimaryButton
                type="primary"
                icon={
                  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="#000000" aria-hidden="true">
                    <path d="M482 152h60q8 0 8 8v704q0 8-8 8h-60q-8 0-8-8V160q0-8 8-8z"></path>
                    <path d="M192 474h672q8 0 8 8v60q0 8-8 8H160q-8 0-8-8v-60q0-8 8-8z"></path>
                  </svg>
                }
                onClick={() => setIsAddModalOpen(true)}
                className="!bg-[#E3AF25] !text-black"
              >
                ADD POSITION
              </PrimaryButton>
            )}
          </div>

          {jobPositions.isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-8xl mx-auto">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-zinc-900 overflow-hidden p-6">
                  <Skeleton active paragraph={{ rows: 4 }} />
                </div>
              ))}
            </div>
          )}

          {jobPositions.isError && (
            <div className="text-center py-8">
              <Title level={4} className="!text-white !font-['Android_101']">
                ERROR LOADING POSITIONS
              </Title>
              <Button
                onClick={() => jobPositions.refetch()}
                className="mt-4 !bg-[#E3AF25] !text-black"
              >
                TRY AGAIN
              </Button>
            </div>
          )}

          {!jobPositions.isLoading && !jobPositions.isError && (
            <div className="position-grid">
              {filteredPositions.length > 0 ? (
                filteredPositions.map((position) => (
                  <div
                    key={position.id}
                    className={`styled-card bg-zinc-900 overflow-hidden relative ${position.featured ? 'position-featured' : ''}`}
                    onClick={() => { viewPositionDetails(position.id); window.scrollTo(0, 0); }}
                  >
                    {isAuthenticated && (
                      <div className="action-buttons">
                        <Tooltip title="Edit position">
                          <Button
                            icon={<EditOutlined />}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditPosition(position.id);
                            }}
                            className="edit-button"
                          />
                        </Tooltip>
                        <Tooltip title="Remove position">
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDelete(position.id);
                            }}
                            className="delete-button"
                          />
                        </Tooltip>
                      </div>
                    )}

                    {position.featured && (
                      <Tag className="featured-tag">FEATURED</Tag>
                    )}

                    <div className="relative p-6 pb-2 h-full flex flex-col">
                      <Title
                        level={5}
                        className={`position-title !text-white pb-2 !font-['Android_101'] !uppercase !font-normal ${position.featured ? 'mt-6' : ''}`}
                      >
                        {position.title}
                      </Title>

                      <div className="flex items-center pb-4 !font-['Barlow']">
                        <img src="/assets/icons/location-icon.svg" alt="Location" className="mr-2 w-6 h-6" />
                        <span className="!font-['Barlow'] text-lg text-white">{position.location}</span>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <Tag className="!bg-transparent !text-white !border-white !rounded-none !font-['Barlow']">{position.department}</Tag>
                        <Tag className="!bg-transparent !text-white !border-white !rounded-none !font-['Barlow']">{position.type}</Tag>
                      </div>

                      {/* Salary and Posted Date */}
                      <div className="flex flex-col gap-2 mb-4 mt-auto">
                        {/* Salary */}
                        <div className="flex items-center text-[#E3AF25] !font-['Barlow']">
                          <img src="/assets/icons/dollar-icon.svg" alt="Salary" className="mr-2" />
                          <span className="!font-['Barlow'] text-lg">{position.salaryRange}</span>
                        </div>

                        {/* Posted Date */}
                        <div className="flex items-center text-gray-400 !font-['Barlow']">
                          <img src="/assets/icons/clock-icon.svg" alt="Posted date" className="mr-2 w-6 h-6" />
                          <span className="!font-['Barlow'] text-sm">Posted on {position.datePosted}</span>
                        </div>
                      </div>
                    </div>

                    {/* View Details Button */}
                    <div className="bg-black p-4 text-center">
                      <BlackButton className="view-details-button">
                        VIEW DETAILS
                      </BlackButton>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-3 text-center py-8">
                  <Title level={4} className="!text-white !font-['Android_101']">
                    NO POSITIONS FOUND
                  </Title>
                  <Paragraph className="text-gray-300 !font-['Barlow']">
                    We couldn't find any positions in the selected category. Please try another category or check back later.
                  </Paragraph>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="bg-first-dark-gray w-full pb-12">
        <div className="container mx-auto pb-8">
          <Title level={2} className="font-android !font-normal text-yellow-principal text-center px-4">WHY WORK WITH US</Title>
          <Paragraph className="font-barlow text-gray-300 text-center px-4">
            At Ruppert Inc., we believe in providing a supportive environment where team members can thrive.
          </Paragraph>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto px-4 md:px-0 mt-12">
            {/* Competitive Compensation */}
            <div className="bg-zinc-800 p-8 flex flex-col gap-4">
              <DollarOutlined className="w-16 h-10 text-yellow-principal text-3xl" />
              <div>
                <div className="font-android !font-normal text-white text-xl">COMPETITIVE COMPENSATION</div>
                <div className="font-barlow text-gray-300 mt-2">
                  We offer competitive salary packages, performance bonuses, and opportunities for advancement based on your contributions and growth.
                </div>
              </div>
            </div>
            {/* Health & Wellness */}
            <div className="bg-zinc-800 p-8 flex flex-col gap-4">
              <CheckCircleOutlined className="w-16 h-10 text-yellow-principal text-3xl" />
              <div>
                <div className="font-android !font-normal text-white text-xl">HEALTH & WELLNESS</div>
                <div className="font-barlow text-gray-300 mt-2">
                  Comprehensive health insurance including medical, dental, and vision coverage to keep you and your family healthy.
                </div>
              </div>
            </div>
            {/* Professional Development */}
            <div className="bg-zinc-800 p-8 flex flex-col gap-4">
              <CheckCircleOutlined className="w-16 h-10 text-yellow-principal text-3xl" />
              <div>
                <div className="font-android !font-normal text-white text-xl">PROFESSIONAL DEVELOPMENT</div>
                <div className="font-barlow text-gray-300 mt-2">
                  Ongoing training programs, certification support, and opportunities to develop new skills and advance your career.
                </div>
              </div>
            </div>
            {/* Work-Life Balance */}
            <div className="bg-zinc-800 p-8 flex flex-col gap-4">
              <CheckCircleOutlined className="w-16 h-10 text-yellow-principal text-3xl" />
              <div>
                <div className="font-android !font-normal text-white text-xl">WORK-LIFE BALANCE</div>
                <div className="font-barlow text-gray-300 mt-2">
                  Flexible schedules when possible, and an understanding that life happens outside of work.
                </div>
              </div>
            </div>
            {/* Team Environment */}
            <div className="bg-zinc-800 p-8 flex flex-col gap-4">
              <CheckCircleOutlined className="w-16 h-10 text-yellow-principal text-3xl" />
              <div>
                <div className="font-android !font-normal text-white text-xl">TEAM ENVIRONMENT</div>
                <div className="font-barlow text-gray-300 mt-2">
                  Join a collaborative team where your input is valued and where we celebrate successes together.
                </div>
              </div>
            </div>
            {/* Safety First Culture */}
            <div className="bg-zinc-800 p-8 flex flex-col gap-4">
              <CheckCircleOutlined className="w-16 h-10 text-yellow-principal text-3xl" />
              <div>
                <div className="font-android !font-normal text-white text-xl">SAFETY FIRST CULTURE</div>
                <div className="font-barlow text-gray-300 mt-2">
                  Your wellbeing is our priority with comprehensive safety training, equipment, and protocols.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="w-full py-20 bg-second-black-carbon" id="application-form">
        <div className="container mx-auto px-4">
          <Title level={2} className="font-android !font-normal text-yellow-principal text-center">APPLY NOW</Title>
          <Paragraph className="font-barlow text-gray-300 text-center pb-8">
            Can't find the right position? Send us your resume and we'll keep you in mind for future opportunities.
          </Paragraph>
          <div className="max-w-2xl mx-auto bg-zinc-900 p-6">

            <Form
              name="job-application"
              layout="vertical"
              form={form}
              onFinish={onFinish}
              requiredMark={false}
              disabled={createJobApplication.isPending}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="font-android !font-normal text-white">FULL NAME</span>}
                    name="fullName"
                    rules={[{ required: true, message: 'Please enter your full name' }]}
                  >
                    <Input className="bg-zinc-800 font-barlow border-zinc-700" placeholder="Your full name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="font-android !font-normal text-white">EMAIL</span>}
                    name="email"
                    rules={[
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input className="bg-zinc-800 font-barlow border-zinc-700" placeholder="Your email" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="font-android !font-normal text-white">PHONE</span>}
                    name="phone"
                    rules={[{ required: true, message: 'Please enter your phone number' }]}
                  >
                    <InputMask
                      mask="usphone"
                      className="bg-zinc-800 font-barlow border-zinc-700"
                      placeholder="Your phone number"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span className="font-android !font-normal text-white">POSITION YOU'RE APPLYING FOR</span>}
                    name="positionId"
                    rules={[{ required: true, message: 'Please select a position' }]}
                  >
                    <select className="w-full p-1 bg-zinc-800 font-barlow">
                      <option value="">Select a position</option>
                      {jobPositions.data?.map(position => (
                        <option key={position.id} value={position.id}>{position.title}</option>
                      ))}
                    </select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label={<span className="font-android !font-normal text-white">TELL US ABOUT YOURSELF</span>}
                name="message"
                rules={[{ required: true, message: 'Please tell us why you want to work with us' }]}
              >
                <TextArea rows={4} className="font-barlow bg-zinc-800 border-zinc-700" placeholder="Your message" />
              </Form.Item>

              <Form.Item className="w-fit"
                label={<span className="font-android !font-normal text-white">ATTACH RESUME</span>}
                name="resume"
                rules={[{ required: true, message: 'Please upload your resume' }]}
              >
                <div>
                  <Upload
                    name="resume"
                    listType="text"
                    accept={FILE_ACCEPT_STRING}
                    beforeUpload={handleResumeUpload}
                    maxCount={1}
                    showUploadList={{
                      showRemoveIcon: true,
                      removeIcon: <DeleteOutlined />,
                    }}
                  >
                    <Button className="!border-gray-700"
                      icon=<UploadOutlined />
                    >
                      <span className="font-android !font-normal text-white">CHOOSE FILE</span>
                    </Button>
                  </Upload>
                </div>
              </Form.Item>
              <PrimaryButton
                type="primary"
                htmlType="submit"
                style={{ marginRight: 0 }}
                loading={createJobApplication.isPending}
              >
                {createJobApplication.isPending ? 'SUBMITTING...' : 'SUBMIT APPLICATION'}
              </PrimaryButton>
            </Form>
          </div>
        </div>
      </section >

      {/* Modal de Adicionar Posição */}
      < AddJobPositionModal
        isOpen={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        onSave={handleAddPosition}
      />

      {/* Modal de Editar Posição */}
      {
        isEditModalOpen && selectedPositionId && (
          <AddJobPositionModal
            isOpen={isEditModalOpen}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedPositionId(null);
            }}
            onSave={(position) => handleUpdatePosition(position)}
            initialValues={jobPositions.data?.find(pos => pos.id === selectedPositionId)}
          />
        )
      }

      {/* Delete Confirmation Modal */}
      <Modal
        title={<span className="font-android !font-normal">CONFIRM DELETE</span>}
        open={!!positionToDelete}
        onCancel={() => setPositionToDelete(null)}
        className="delete-confirm-modal"
        footer={[
          <Button
            key="cancel"
            onClick={() => setPositionToDelete(null)}
            className="font-barlow text-white mr-2"
          >
            <span className="text-white">CANCEL</span>
          </Button>,
          <PrimaryButton
            key="delete"
            danger
            onClick={handleDelete}
            className="font-barlow text-black"
          >
            DELETE
          </PrimaryButton>
        ]}
      >
        <p className="font-barlow">Are you sure you want to mark this position as deleted? This action cannot be undone.</p>
      </Modal>

      {/* Modal de Agradecimento */}
      <Modal
        title={null}
        open={isThankYouVisible}
        footer={null}
        closable={false}
        centered
        className="application-thank-you-modal"
        width={600}
        maskClosable={true}
        onCancel={handleCloseThankYou}
      >
        <div className="text-center p-4">
          <CheckCircleOutlined className="text-[#E3AF25] text-6xl mb-6" />
          <Title level={3} className="font-android !font-normal !text-white mb-6">THANK YOU FOR YOUR APPLICATION</Title>
          <Paragraph className="font-barlow text-gray-300 text-lg mb-6">
            We've received your application for the <span className="text-yellow-principal">{appliedPositionTitle}</span> position. Our team will carefully review your resume and qualifications.
          </Paragraph>
          <Paragraph className="font-barlow text-gray-400 text-sm">
            This message will close automatically in a few seconds...
          </Paragraph>
        </div>
      </Modal>
    </div >
  );
};
